import { omit } from "lodash";
import type { ComponentPropsWithRef } from "react";
import { forwardRef, useMemo } from "react";

import { classNames } from "@/react/helpers/twMergeWithCN";
import type { FieldElement } from "@/react/types/Element";

import { useProfileFieldPrefill } from "../shared/useProfileFieldPrefill";
import { DropdownElement as DropdownElementComponent } from "./DropdownElement";

interface DropdownBlockMeta {
  elementableId: number;
  elementId: number;
  fieldIndex: number;
  position: number;
  profileFieldId?: number | null;
}

interface Option {
  id: string;
  value: string;
}

export interface FormDropdownBlockProps extends ComponentPropsWithRef<"div"> {
  blockInstanceMeta?: DropdownBlockMeta;
  label?: string;
  options?: Option[];
  required?: boolean;
}

export const FormDropdownBlock = forwardRef<
  HTMLDivElement,
  FormDropdownBlockProps
>(
  (
    {
      label = "Dropdown",
      required = false,
      options = [
        { id: "1", value: "Option 1" },
        { id: "2", value: "Option 2" },
        { id: "3", value: "Option 3" },
      ],
      className,
      blockInstanceMeta = {},
      ...rest
    },
    ref,
  ) => {
    const elementProps = omit(rest, ["blockPath"]);
    const { context, rawPrefillValue } =
      useProfileFieldPrefill(blockInstanceMeta);
    const fallbackIndex = useMemo(() => Date.now(), []);

    const prefillValue = Array.isArray(rawPrefillValue)
      ? rawPrefillValue[0]
      : rawPrefillValue;

    const optionValues = options.map(option => option.value);

    const element: FieldElement = {
      id: blockInstanceMeta.elementId ?? 0,
      elementable_id: blockInstanceMeta.elementableId ?? 0,
      position: blockInstanceMeta.position ?? 0,
      type: "field",
      attributes: {
        field_type: "dropdown",
        label,
        placeholder: null,
        required,
        hidden: false,
        options: optionValues,
      },
    };

    const errors =
      "backendErrors" in context ? (context.backendErrors ?? {}) : {};

    return (
      <div
        ref={ref}
        className={classNames("w-full", className)}
        {...elementProps}
      >
        <DropdownElementComponent
          element={element}
          fieldIndex={blockInstanceMeta.fieldIndex ?? fallbackIndex}
          errors={errors}
          defaultValue={
            prefillValue && typeof prefillValue === "string"
              ? prefillValue
              : undefined
          }
        />
      </div>
    );
  },
);

FormDropdownBlock.displayName = "FormDropdownBlock";
